import { getDbSupabase } from './supabase';

export type CffCustomerLookup = {
  no: string;
  name: string;
  address: string;
  post_code: string;
  territory_code: string;
  contact: string;
  phone_no: string;
  /** Nav / PowerBI customer price group when available. */
  customer_price_group?: string;
};

export type CffItemLookup = {
  no: string;
  description: string;
  sales_uom: string;
};

async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  try {
    const { data } = await getDbSupabase('forms').auth.getSession();
    const token = data.session?.access_token?.trim();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // proxy rejects unauthenticated
  }
  return headers;
}

export async function searchCffCustomers(query: string): Promise<CffCustomerLookup[]> {
  const q = query.trim();
  if (!q) return [];
  const headers = await authHeaders();
  const response = await fetch(
    `/cff-api?resource=customers&q=${encodeURIComponent(q)}&limit=40`,
    { headers },
  );
  const json = (await response.json().catch(() => null)) as
    | { customers?: CffCustomerLookup[]; error?: string }
    | null;
  if (!response.ok) {
    throw new Error(json?.error || `Customer search failed (${response.status}).`);
  }
  const customers = Array.isArray(json?.customers) ? json.customers : [];
  return customers.map((row) => ({
    no: String(row.no ?? '').trim(),
    name: String(row.name ?? '').trim(),
    address: String(row.address ?? '').trim(),
    post_code: String(row.post_code ?? '').trim(),
    territory_code: String(row.territory_code ?? '').trim(),
    contact: String(row.contact ?? '').trim(),
    phone_no: String(row.phone_no ?? '').trim(),
    customer_price_group: String(
      (row as CffCustomerLookup).customer_price_group ?? '',
    ).trim(),
  }));
}

export async function searchCffItems(query: string): Promise<CffItemLookup[]> {
  const q = query.trim();
  if (!q) return [];
  const headers = await authHeaders();
  const response = await fetch(`/cff-api?resource=items&q=${encodeURIComponent(q)}&limit=40`, {
    headers,
  });
  const json = (await response.json().catch(() => null)) as
    | { items?: CffItemLookup[]; error?: string }
    | null;
  if (!response.ok) {
    throw new Error(json?.error || `Item search failed (${response.status}).`);
  }
  return Array.isArray(json?.items) ? json.items : [];
}

export type CffInvoiceLookup = {
  document_no: string;
  posting_date: string;
  creator: string;
  invoice_issued_by: string;
  /** Sell-to customer from the same posted_sales_invoice row, when present. */
  customer: CffCustomerLookup | null;
};

/** Look up posted sales invoice by No. Returns null when not found. */
export async function lookupCffInvoice(invoiceNo: string): Promise<CffInvoiceLookup | null> {
  const no = invoiceNo.trim();
  if (!no) return null;
  const headers = await authHeaders();
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 20000);
  try {
    const response = await fetch(`/cff-api?resource=invoice&no=${encodeURIComponent(no)}`, {
      headers,
      signal: controller.signal,
    });
    const json = (await response.json().catch(() => null)) as
      | { invoice?: (CffInvoiceLookup & { customer?: CffCustomerLookup | null }) | null; error?: string }
      | null;
    if (!response.ok) {
      throw new Error(json?.error || `Invoice lookup failed (${response.status}).`);
    }
    const invoice = json?.invoice;
    if (!invoice || typeof invoice !== 'object') return null;
    const rawCustomer = invoice.customer;
    let customer: CffCustomerLookup | null = null;
    if (rawCustomer && typeof rawCustomer === 'object' && String(rawCustomer.no ?? '').trim()) {
      customer = {
        no: String(rawCustomer.no ?? '').trim(),
        name: String(rawCustomer.name ?? '').trim(),
        address: String(rawCustomer.address ?? '').trim(),
        post_code: String(rawCustomer.post_code ?? '').trim(),
        territory_code: String(rawCustomer.territory_code ?? '').trim(),
        contact: String(rawCustomer.contact ?? '').trim(),
        phone_no: String(rawCustomer.phone_no ?? '').trim(),
      };
    }
    return {
      document_no: String(invoice.document_no ?? '').trim(),
      posting_date: String(invoice.posting_date ?? '').trim(),
      creator: String(invoice.creator ?? '').trim(),
      invoice_issued_by: String(invoice.invoice_issued_by ?? '').trim(),
      customer,
    };
  } finally {
    window.clearTimeout(timer);
  }
}
